import type { Recipe } from '../types';
import type { RuntimeProductFunnel } from '../types/config';
import type { PetSnapshot } from '../types/pets';

/** productId → maximum units for the first shipment. Absent key = no limit; 0 = sold out for this order. */
export type StockLimits = Record<string, number>;

export interface BuildStockLimitsArgs {
  products: RuntimeProductFunnel[];
  petsCompleted: PetSnapshot[];
  /** productId → units this pet reserves beyond the first shipment (the monthly cans
   *  when inventory_rules.stock_reserve_scope also covers the month). */
  reserve?: Record<string, number>;
}

// Units the already-completed pets take from each product in the first shipment —
// the same aggregate the backend validates against.
function unitsReservedByCompletedPets(petsCompleted: PetSnapshot[]): Record<string, number> {
  const reserved: Record<string, number> = {};
  for (const pet of petsCompleted) {
    const distribution = pet.formData?.initiationPackDistribution || {};
    for (const [productId, qty] of Object.entries(distribution)) {
      if (!Number.isFinite(qty) || qty <= 0) continue;
      reserved[productId] = (reserved[productId] ?? 0) + qty;
    }
  }
  return reserved;
}

// The backend publishes `stock_available_qty` with every stock rule already applied
// (safety buffer, tracking, verification toggle) — the same source as its checkout
// quantity gate. Here only the other pets' first shipment and this pet's reserve
// beyond the first shipment are subtracted.
export function buildStockLimits({ products, petsCompleted, reserve = {} }: BuildStockLimitsArgs): StockLimits {
  const reservedByOthers = unitsReservedByCompletedPets(petsCompleted);
  const limits: StockLimits = {};
  for (const product of products) {
    const qty = product.stock_available_qty;
    if (qty == null || !Number.isFinite(qty)) continue;
    const rawReserve = reserve[product.id];
    const heldByThisPet = Number.isFinite(rawReserve) ? Math.max(0, Math.floor(rawReserve)) : 0;
    const held = (reservedByOthers[product.id] ?? 0) + heldByThisPet;
    limits[product.id] = Math.max(0, Math.floor(qty) - held);
  }
  return limits;
}

// The monthly plan is keyed by the product the plan resolved (one size per recipe). When the
// tutor swaps a line to its other size in the editor, the backend gate re-plans that recipe's
// monthly in the new size, so the reserve must follow the swap: convert the units by grams to
// every size sibling of the same recipe (kcal per gram is the same across sizes).
export function monthlyReserveBySize(monthly: Record<string, number>, recipes: Recipe[]): Record<string, number> {
  const reserve: Record<string, number> = {};
  for (const [productId, units] of Object.entries(monthly)) {
    if (!Number.isFinite(units) || units <= 0) continue;
    reserve[productId] = (reserve[productId] ?? 0) + units;
    const owner = recipes.find((recipe) =>
      Object.values(recipe.variants || {}).some((variant) => String(variant.productId) === productId),
    );
    if (!owner?.variants) continue;
    const sizes = Object.entries(owner.variants).map(([key, variant]) => ({
      productId: String(variant.productId),
      grams: parseInt(key, 10),
    }));
    const own = sizes.find((size) => size.productId === productId);
    if (!own || !own.grams) continue;
    for (const sibling of sizes) {
      if (sibling.productId === productId || !sibling.grams) continue;
      reserve[sibling.productId] = (reserve[sibling.productId] ?? 0) + Math.ceil((units * own.grams) / sibling.grams);
    }
  }
  return reserve;
}

export function stockLimitFor(limits: StockLimits | undefined, productId: string): number | null {
  if (!limits) return null;
  const limit = limits[productId];
  return typeof limit === 'number' ? limit : null;
}
